import { loadSchema } from "@fatbees/schemas";
import { toast } from "@fatbees/ui-components";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { deleteLoad, putLoad } from "../api";

export function useLoadsCollectionInstance(queryClient: QueryClient) {
  return useMemo(() => {
    return createCollection(
      queryCollectionOptions({
        id: "loads",
        queryKey: ["loads"],
        syncMode: "eager",
        queryFn: async () => {
          const response = await fetch("/api/loads");
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = await response.json();

          return body.map((load: unknown) => loadSchema.parse(load));
        },
        queryClient,
        getKey: (item) => item.id,
        schema: loadSchema,
        onInsert: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putLoad(modified);

                collection.utils.writeInsert([modified]);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save load", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onUpdate: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putLoad(modified);

                collection.utils.writeUpdate(modified);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save load", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onDelete: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await deleteLoad(modified);

                collection.utils.writeDelete(modified.id);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save load", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
      }),
    );
  }, [queryClient]);
}

export type LoadsCollection = ReturnType<typeof useLoadsCollectionInstance>;
